/**
 *
 * PortalTable
 *
 * @version 1.0.0
 * @author Giuseppe
 * @since 14-Oct-2022
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { generateUniqueId } from 'Utility/generateUniqueID';
import PortalNoElementFound from 'Components/PortalNoElementFound';
import {
	TablePagination,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TableContainer,
	Paper,
	Typography,
	Skeleton,
} from '@mui/material';
import AdditionalColumnsPopup from './AdditionalColumnsPopup/AdditionalColumnsPopup';
import PortalCheckbox from 'Components/PortalCheckbox';
import PortalAnchorButton from 'Components/PortalAnchorButton';
import formattedMessages from './languageModule';
import { Grid } from '@mui/material';
import style from './PortalTable.scss';

const IconButton = (icon, onClick) => (
	<i
		className={icon}
		onClickCapture={(e) => {
			e.stopPropagation();
			onClick();
		}}
	/>
);

const ActionButtons = (buttonProps) => (
	<Grid className={style.actions}>
		{buttonProps ? (
			<>
				{buttonProps.editCondition ? IconButton('nexi-icons-pencil', buttonProps.onClickEdit) : null}
				{buttonProps.deleteCondition ? IconButton('nexi-icons-trash-bin', buttonProps.onClickDelete) : null}
				{buttonProps.disableCondition ? IconButton('nexi-icons-ban-signal', buttonProps.onClickDisable) : null}
				{buttonProps.enableCondition ? IconButton('nexi-icons-circle-check', buttonProps.onClickEnable) : null}
			</>
		) : null}
	</Grid>
);

const PortalTable = (props) => {
	const [extraColumnsModalOpened, setExtraColumnsModalOpened] = useState(false);
	const [extraHeaders, setExtraHeaders] = useState([...new Array(props.extraHeaders.length)].map(() => null));
	const intl = useIntl();

	const addOrRemoveProperty = (key, obj, payload) => {
		if (obj[key]) {
			const { [key]: _, ...newObj } = obj;
			return newObj;
		}
		return { ...obj, [key]: payload || true };
	};

	const setRowsCheckedHandler = (e, payload) => {
		const targetId = e.target.id;
		if (targetId) {
			const newState = addOrRemoveProperty(e.target.id, props.rowsChecked, payload);
			props.setRowsChecked(newState);
			props.setIsAllChecked(Object.keys(newState).length === props.tBodyItems.length);
		}
	};

	const setIsAllCheckedHandler = (e) => {
		props.setIsAllChecked(!props.isAllChecked && props.valueOfCheckedAll ? props.valueOfCheckedAll : e.target.checked);
		props.setRowsChecked({}); //reset items
	};

	const generateSkeletonRows = (rowsNumber) => {
		const skeletonItems = [];

		for (let i = 0; i < rowsNumber; i++) {
			skeletonItems.push(
				<TableRow key={`table-body-row-loading-0-${i}`}>
					<TableCell key={`table-head-cell-loading-1-${i}`}>
						<Skeleton className={style.skeletonRow} animation='wave' />
					</TableCell>
					<TableCell key={`table-head-cell-loading-2-${i}`}>
						<Skeleton className={style.skeletonRow} animation='wave' />
					</TableCell>
					<TableCell key={`table-head-cell-loading-3-${i}`}>
						<Skeleton className={style.skeletonRow} animation='wave' />
					</TableCell>
				</TableRow>
			);
		}

		return skeletonItems;
	};

	const generateHeadTableCell = (tHeadArray) =>
		tHeadArray.map((tHeadTitle, index) =>
			tHeadTitle ? (
				<TableCell key={`table-head-cell-${props.key}-${index}`}>{intl.formatMessage(tHeadTitle)}</TableCell>
			) : null
		);

	const generateBodyTableCell = (arrayItems, pieceOfStringToAdd, rootArray) => {
		const arrayItemsLength = arrayItems.length;
		const extraHeaderLength = extraHeaders.length;

		const itemToShow = [];

		for (let i = 0; i < arrayItemsLength; i++) {
			if (
				(extraHeaderLength !== arrayItemsLength && i < arrayItemsLength - extraHeaderLength) ||
				extraHeaders[i - (arrayItemsLength - extraHeaderLength)]
			) {
				itemToShow.push(
					<TableCell key={`table-${pieceOfStringToAdd}-cell-${props.key}-${i}`}>
						{arrayItems[i] && arrayItems[i].formattedMessageScope
							? intl.formatMessage({
									id: `${arrayItems[i].formattedMessageScope}.${rootArray[arrayItems[i].fieldName]}`,
									defaultMessage: [{ type: 0, value: '-' }],
							  })
							: rootArray[arrayItems[i].fieldName]}
					</TableCell>
				);
			}
		}

		return itemToShow;
	};
	return (
		<>
			<Grid className={style.wrapper}>
				{!props.isLoading ? (
					props.title ||
					(props.extraAction && props.extraAction.length > 0) ||
					(props.extraHeaders && props.extraHeaders.length > 0) ? (
						<Grid className={style.infoHead}>
							{props.title ? <Typography className={style.title}>{intl.formatMessage(props.title)}</Typography> : null}
							<Grid className={style.extraAction}>
								{props.extraAction
									? props.extraAction.map((element, index) =>
											props.tBodyItems.length > 0 || element.forcedShow ? (
												<PortalAnchorButton
													key={`extra-anchor-${index}`}
													textTransform='isUppercase'
													contentText={element.label}
													startIcon={element.icon}
													onClickAction={element.action}
													isDisabled={element.isDisabled || false}
												/>
											) : null
									  )
									: null}
								{props.extraHeaders.length > 0 && props.tBodyItems.length > 0 ? (
									<PortalAnchorButton
										textTransform='isUppercase'
										contentText={formattedMessages.addColumns}
										startIcon='nexi-icons-vertical-columns'
										onClickAction={() => setExtraColumnsModalOpened(true)}
									/>
								) : null}
							</Grid>
						</Grid>
					) : null
				) : (
					<Grid className={`${style.infoHead} ${style.skeleton}`}>
						<Skeleton className={style.skeletonRow} animation='wave' />
					</Grid>
				)}
				{!props.isLoading && props.tBodyItems.length === 0 ? (
					<PortalNoElementFound />
				) : (
					<Grid
						className={`
						${style.tableContainer}
						${props.simpleTable ? style.simpleTable : ''}
						${props.rowOnClick ? style.handOnRow : ''}
					`}>
						<TableContainer className={style.container} component={Paper}>
							<Table aria-label={props.key} className={`${props.isLoading ? style.layoutFixed : ''}`}>
								<TableHead>
									{props.isLoading ? (
										generateSkeletonRows(1)
									) : (
										<TableRow key={`table-head-row-${props.key}`}>
											{/* Checkbox Cell */}
											{props.isTableClickable ? (
												<TableCell key={`table-head-cell-checkbox-${props.key}`}>
													<PortalCheckbox
														key={`table-head-checkbox-${props.key}`}
														onClick={(e) => setIsAllCheckedHandler(e)}
														checked={props.isAllChecked}
													/>
												</TableCell>
											) : null}
											{/* Data Cell */}
											{generateHeadTableCell(props.tHeadItems)}
											{/* Extra Header Cell */}
											{extraHeaders.length > 0 ? generateHeadTableCell(extraHeaders) : null}
											{/* Action Cell */}
											{props.buttonActions ? <TableCell key={`table-head-cell-empty${props.key}`} /> : null}
										</TableRow>
									)}
								</TableHead>
								<TableBody>
									{props.isLoading
										? generateSkeletonRows(10)
										: props.tBodyItems.length > 0 &&
										  props.tBodyItems.map((tBodyItems, rowIndex) => (
												<TableRow
													key={`table-body-row-${props.key}-${rowIndex}`}
													onClick={
														!props.isTableClickable
															? props.rowOnClick
																? (e) => props.rowOnClick(e, tBodyItems)
																: () => {}
															: () => {}
													}
													selected={props.rowsChecked[`${props.key}-${rowIndex}`] || props.isAllChecked}>
													{/* Checkbox Cell */}
													{props.isTableClickable ? (
														<TableCell key={`table-body-cell-checkbox-${props.key}-${rowIndex}`}>
															<PortalCheckbox
																key={`table-body-checkbox-${props.key}-${rowIndex}`}
																onClickCapture={(e) => {
																	e.stopPropagation();
																	setRowsCheckedHandler(e, tBodyItems);
																}}
																checked={props.rowsChecked[`${props.key}-${rowIndex}`] || props.isAllChecked}
																id={`${props.key}-${rowIndex}`}
															/>
														</TableCell>
													) : null}
													{/* Data Cell */}
													{generateBodyTableCell(props.tBodyItemsToShow, 'body', tBodyItems)}
													{/* Action Cell */}
													{props.buttonActions ? (
														<TableCell key={`table-body-cell-action-buttons-${props.key}`}>
															<ActionButtons
																key={props.key}
																onClickEdit={() => props.buttonActions.onClickEdit(tBodyItems)}
																editCondition={
																	props.buttonActions.editCondition
																		? props.buttonActions.editCondition(tBodyItems)
																		: false
																}
																onClickDelete={() => props.buttonActions.onClickDelete(tBodyItems)}
																deleteCondition={
																	props.buttonActions.deleteCondition
																		? props.buttonActions.deleteCondition(tBodyItems)
																		: false
																}
																onClickDisable={() => props.buttonActions.onClickDisable(tBodyItems)}
																disableCondition={
																	props.buttonActions.disableCondition
																		? props.buttonActions.disableCondition(tBodyItems)
																		: false
																}
																onClickEnable={() => props.buttonActions.onClickEnable(tBodyItems)}
																enableCondition={
																	props.buttonActions.enableCondition
																		? props.buttonActions.enableCondition(tBodyItems)
																		: false
																}
															/>
														</TableCell>
													) : null}
												</TableRow>
										  ))}
								</TableBody>
							</Table>
						</TableContainer>
						{props.pagination ? (
							<Grid className={style.paginationContainer}>
								<Typography variant='body1' className={style.totalElements}>
									{!props.isLoading
										? `${intl.formatMessage(formattedMessages.totalElements)} ${props.pagination.totalElements}`
										: ''}
								</Typography>
								<TablePagination
									component='div'
									count={props.pagination.totalElements}
									rowsPerPage={props.pageSize}
									page={props.pagination.pageNumber - 1} //from 0 (Material constraint)
									onPageChange={(_, pageNumber) => props.onPageChange(pageNumber + 1)} //increment pageNumber from 0 to 1
									rowsPerPageOptions={props.rowsPerPageOptions}
									onRowsPerPageChange={(e) => props.onRowsPerPageChange(e)}
									labelRowsPerPage={intl.formatMessage(formattedMessages.rowsPerPage)}
									SelectProps={{ native: true }}
									labelDisplayedRows={({ page }) =>
										`
                    ${intl.formatMessage(formattedMessages.page)}
                    ${!props.isLoading ? page + 1 : '-'}
                    ${intl.formatMessage(formattedMessages.ofPage)} ${
											!props.isLoading ? props.pagination.totalPages : '-'
										}
                  `
									}
									showFirstButton={props.pagination.hasPrevious}
									showLastButton={props.pagination.hasNext}
								/>
							</Grid>
						) : null}
					</Grid>
				)}
			</Grid>

			<AdditionalColumnsPopup
				open={extraColumnsModalOpened}
				onCloseModal={() => setExtraColumnsModalOpened(false)}
				extraHeaders={props.extraHeaders}
				extraHeadersSetter={setExtraHeaders}
			/>
		</>
	);
};

PortalTable.propTypes = {
	actions: PropTypes.objectOf(PropTypes.func),
	key: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
	tHeadItems: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
	tBodyItems: PropTypes.array,
	buttonActions: PropTypes.exact({
		onClickEdit: PropTypes.func,
		editCondition: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
		onClickDelete: PropTypes.func,
		deleteCondition: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
		onClickDisable: PropTypes.func,
		disableCondition: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
		onClickEnable: PropTypes.func,
		enableCondition: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	}),
	isTableClickable: PropTypes.bool,
	pagination: PropTypes.shape({
		pageNumber: PropTypes.number,
		hasNext: PropTypes.bool,
		hasPrevious: PropTypes.bool,
		totalPages: PropTypes.number,
		totalElements: PropTypes.number,
		size: PropTypes.number,
	}),
	pageSize: PropTypes.number,
	onRowsPerPageChange: PropTypes.func,
	onPageChange: PropTypes.func,
	isLoading: PropTypes.bool.isRequired,
	simpleTable: PropTypes.bool,
	rowOnClick: PropTypes.func,
	rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
	title: PropTypes.string,
	extraAction: PropTypes.arrayOf(PropTypes.object),
	tBodyItemsToShow: PropTypes.arrayOf(
		PropTypes.exact({
			fieldName: PropTypes.string,
			formattedMessageScope: PropTypes.string,
		})
	).isRequired,
	extraHeaders: PropTypes.arrayOf(PropTypes.exact({ id: PropTypes.string })),
	setRowsChecked: PropTypes.func,
	setIsAllChecked: PropTypes.func,
	rowsChecked: PropTypes.object,
	isAllChecked: PropTypes.bool,
	valueOfCheckedAll: PropTypes.string,
};

PortalTable.defaultProps = {
	key: generateUniqueId(),
	isTableClickable: false,
	simpleTable: false,
	rowsPerPageOptions: [10, 20, 30],
	title: '',
	extraAction: [],
	extraHeaders: [],
	setRowsChecked: () => {},
	setIsAllChecked: () => {},
	rowsChecked: {},
	isAllChecked: false,
	valueOfCheckedAll: '',
	pageSize: 10,
};

export default PortalTable;
